import React from 'react';
import { useTranslation } from 'react-i18next';

import { RiSearchLine, RiDeleteBin7Fill } from 'react-icons/ri';

import { OrderingSelectors, OrderingOperations, OrderingConstants } from 'polygon-ordering';

import combineStyles from '../utils/combineStyles';
import { TEXT_PROPERTIES, SELECT_PROPERTIES } from '../utils/theme';

import getThemeLookup from '../selectors/getThemeLookup';

import Modal from '../components/Modal';
import Text from '../components/Text';
import StandardButton from '../components/StandardButton';
import OfferInfoButton from '../components/OfferInfoButton';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import EVENTS from '../constants/events';

import { enqueueSuccessSnackbar, enqueueErrorSnackbar } from '../utils/snackbar';
import { logEvent } from '../utils/analytics';

const { getSelectedOffer, getOffers, getPromoCode } = OrderingSelectors;
const { selectOffer, setPromoCode, applyPromoCode, updateSelectedPaymentMethods } =
  OrderingOperations;
const { FAILURE_REASON } = OrderingConstants;

export const OFFER_MODAL_ID = 'OFFER_MODAL_ID';

const OfferModal = () => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const offers = useAppSelector(getOffers);
  const selectedOffer = useAppSelector(getSelectedOffer);
  const promoCode = useAppSelector(getPromoCode);
  const dispatch = useAppDispatch();
  const noOfferPlaceholder = t('placeholder.memberOffers');

  const getModalTitle = () => {
    if (selectedOffer) {
      return t('title.modal.offer.offerPresent');
    }

    if (offers.length) {
      return t('title.modal.offer.offerAbsentOffersPresent');
    }

    return t('title.modal.offer.offerAbsentOffersAbsent');
  };

  return (
    <Modal
      clickBackgroundToClose
      title={getModalTitle()}
      desktopMaxWidth={400}
      desktopMinWidth={400}
      closeButtonLabel={t('button.modalClose.dismiss')}
      containerStyle={styles.containerStyle}
      desktopMinHeight={300}
    >
      {selectedOffer ? (
        <>
          <div style={styles.nameContainer}>
            <Text themeKey="offerName" style={styles.name}>
              {selectedOffer.name.toUpperCase()}
            </Text>

            <OfferInfoButton />
          </div>

          <StandardButton
            themeKey="removeOfferButton"
            label={t('button.removeOffer')}
            onClick={() => {
              dispatch(selectOffer({ offerId: null, userAdded: true }));
              dispatch(updateSelectedPaymentMethods({}));
            }}
            RightIconComponent={RiDeleteBin7Fill}
          />
          <div style={styles.messageContainer}>
            <Text themeKey="orderMessage" style={styles.messageText}>
              {selectedOffer.description}
            </Text>
          </div>
        </>
      ) : (
        <>
          <div style={styles.promoCodeSection}>
            <input
              value={promoCode || ''}
              onChange={event => dispatch(setPromoCode(event?.target?.value))}
              maxLength={15}
              placeholder={t('placeholder.promoCode')}
              style={combineStyles(
                styles.promoCodeInput,
                p('defaultText', TEXT_PROPERTIES),
                p('input', SELECT_PROPERTIES),
              )}
            />

            <StandardButton
              themeKey="lookupPromoCodeButton"
              RightIconComponent={RiSearchLine}
              onClick={async () => {
                await dispatch(applyPromoCode({}))
                  .unwrap()
                  .then(() => {
                    enqueueSuccessSnackbar(t('applyPromoCode.success'));
                    logEvent(EVENTS.APPLY_PROMOCODE);
                  })
                  .catch((err: any) => {
                    if (err.reason === FAILURE_REASON.FETCH_FAILED) {
                      enqueueErrorSnackbar(t('applyPromoCode.notFound'));
                      logEvent(EVENTS.PROMOCODE_NOT_FOUND);
                    } else {
                      enqueueErrorSnackbar(t('applyPromoCode.failure'));
                      logEvent(EVENTS.APPLY_PROMOCODE_FAILED, { label: err.reason });
                    }
                  });
              }}
              disabled={!promoCode}
              containerStyle={styles.applyButton}
            />
          </div>

          {Boolean(offers.length) && (
            <>
              <div style={styles.orContainer}>
                <Text>{t('offerTypeDivider')}</Text>
              </div>

              <select
                onChange={event => {
                  let offerId: string | null = event?.target?.value;

                  if (offerId === noOfferPlaceholder) {
                    offerId = null;
                  }

                  dispatch(selectOffer({ offerId, userAdded: true }))
                    .unwrap()
                    .then(response => {
                      logEvent(response.offerId ? EVENTS.SELECT_OFFER : EVENTS.REMOVE_OFFER, {
                        label: response.purchaseStaged ? 'PURCHASE_STAGED' : null,
                        value: offerId ? parseInt(offerId) : '',
                      });
                    })
                    .catch(err => {
                      let message;

                      if (err.reason === FAILURE_REASON.OFFER_NOT_PRESENT_AND_AVAILABLE) {
                        message = t('selectOfferFailed.unavailable');
                      } else if (err.reason === FAILURE_REASON.COULD_NOT_STAGE_PURCHASE) {
                        message = t('selectOfferFailed.item');
                      } else {
                        message = t('selectOfferFailed.default');
                      }
                      enqueueErrorSnackbar(message);

                      logEvent(EVENTS.SELECT_OFFER_FAILED, { label: err.reason });
                    });

                  dispatch(updateSelectedPaymentMethods({}));
                }}
                style={combineStyles(
                  styles.offerSelector,
                  p('defaultText', SELECT_PROPERTIES),
                  p('input', SELECT_PROPERTIES),
                  p('memberOffers', SELECT_PROPERTIES),
                )}
              >
                <option>{noOfferPlaceholder}</option>

                {offers.map(offer => (
                  <option key={offer.id} value={offer.id}>
                    {offer.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

const styles: Styles = {
  containerStyle: {
    padding: 30,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  nameContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',

    marginTop: 60,
    marginBottom: 30,
  },

  name: {
    textAlign: 'center',
    fontStyle: 'italic',
  },

  promoCodeSection: {
    marginTop: 30,

    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },

  promoCodeInput: {
    textAlign: 'center',
    minHeight: '100%',
    borderRadius: 0,
    outline: 'none',
    border: '1px solid silver',
    borderRightWidth: 0,
  },

  applyButton: {
    borderRadius: 0,
  },

  orContainer: {
    marginTop: 20,
    marginBottom: 20,
  },

  offerSelector: {
    textAlignLast: 'center',
    minWidth: 220,
  },
  messageContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem',
  },
  messageText: {
    textAlign: 'center',
    fontStyle: 'italic',
    fontSize: '0.7rem',
    fontWeight: 200,
    textTransform: 'capitalize',
  },
};

export default OfferModal;
